import styles from "../WorkoutPlanDialog.module.scss";
import { useExercisePicker } from "../hooks/useExercisePicker";
import {
  DistanceUnit,
  IExerciseSearchResultModel,
  WorkoutExerciseType,
} from "../models/workoutPlanViewModel";
import { formatSetSummary } from "../utils/setValueFormat";

/** The user's last set on the exercise, read the way that set was measured. */
const lastSetLabel = (result: IExerciseSearchResultModel): string | null =>
  formatSetSummary(
    result.lastExerciseType ?? WorkoutExerciseType.KgReps,
    result.lastDistanceUnit ?? DistanceUnit.Meters,
    { kg: result.lastKg, reps: result.lastReps, speedKmh: result.lastSpeedKmh },
  );

interface IWorkoutPlanExercisePickerProps {
  eventId?: number;
  /** Id of the plan being edited; resolves history for standalone (event-less) workouts. */
  planId?: number;
  onPickFromCatalog: (catalogExerciseId: number, exerciseName: string) => Promise<void> | void;
  onCreateAndAdd: (name: string) => Promise<void> | void;
  /**
   * Whether the "+ Създай ново упражнение" row is offered. Only admins may author
   * new studio exercises; owners/users can pick existing ones only.
   */
  canCreateExercise: boolean;
  /**
   * Text shown on an exercise with no recorded history. In the admin view the history
   * belongs to the client ("ново за клиента"); in the owner's own workout it's the
   * viewer's own ("ново за вас").
   */
  noHistoryLabel?: string;
}

export const WorkoutPlanExercisePicker = ({
  eventId,
  planId,
  onPickFromCatalog,
  onCreateAndAdd,
  canCreateExercise,
  noHistoryLabel = "ново за клиента",
}: IWorkoutPlanExercisePickerProps) => {
  const {
    containerRef,
    inputRef,
    query,
    setQuery,
    results,
    showDropdown,
    canOfferCreate,
    dropdownMaxHeight,
    open,
    close,
    pickFromCatalog,
    createAndAdd,
  } = useExercisePicker({ eventId, planId, onPickFromCatalog, onCreateAndAdd });

  // The results overlay always opens upwards. The picker lives at the bottom of
  // the exercise list, so opening downwards would push the menu past the dialog's
  // scroll boundary and clip it. Anchoring it above the input keeps the whole
  // list visible no matter how many results come back.
  const dropdown = showDropdown && (
    <div
      className={styles.picker_dropdown}
      style={{ maxHeight: dropdownMaxHeight }}
      role="listbox"
    >
      {results.map((r) => {
        const last = r.hasUserHistory ? lastSetLabel(r) : null;
        return (
        <div
          key={r.id}
          className={styles.picker_item}
          role="option"
          aria-selected={false}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => pickFromCatalog(r.id, r.name)}
        >
          <div className={styles.picker_item_top}>
            <div className={styles.picker_item_name}>{r.name}</div>
            {last ? (
              <div className={`${styles.picker_pill} ${styles.picker_pill_has}`}>
                последно: {last}
              </div>
            ) : (
              <div className={`${styles.picker_pill} ${styles.picker_pill_no}`}>
                {noHistoryLabel}
              </div>
            )}
          </div>
        </div>
        );
      })}
      {canCreateExercise && canOfferCreate && (
        <div
          className={`${styles.picker_item} ${styles.picker_item_new}`}
          role="option"
          aria-selected={false}
          onMouseDown={(e) => e.preventDefault()}
          onClick={createAndAdd}
        >
          <div className={styles.picker_item_top}>
            <div className={styles.picker_item_name}>
              + Създай ново упражнение „{query.trim()}"
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.picker} ref={containerRef}>
      {dropdown}
      <input
        ref={inputRef}
        type="text"
        className={styles.picker_input}
        placeholder="+ Добави упражнение..."
        value={query}
        onFocus={open}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") close();
        }}
        autoComplete="off"
      />
    </div>
  );
};
